import type { MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import type {
  ObjectKind,
  SchemaElement,
  SchemaRegion,
} from "../../../../api/restaurantSchema";
import { snapToGrid } from "../../../../components/restaurant-schema/geometry";
import SchemaStage from "../../../../components/restaurant-schema/SchemaStage";
import type { LogicalPoint } from "../../../../components/restaurant-schema/stageLayout";
import { useSchemaStage } from "../../../../components/restaurant-schema/useSchemaStage";
import SelectionToolbar from "./SelectionToolbar";
import AddToolbar from "./AddToolbar";
import AlignmentGuideLines from "./AlignmentGuideLines";
import RotationHandle from "./RotationHandle";
import { useElementDrag } from "./useElementDrag";
import { useElementRotate } from "./useElementRotate";

const TOOLBAR_HEIGHT = 46;
// Разстояние между лентата и ръба на избрания елемент (пиксели).
const TOOLBAR_GAP_PX = 12;
// Дръжката за завъртане: на толкова пиксела под елемента (центърът ѝ) и половината ѝ размер.
const ROTATION_HANDLE_GAP_PX = 30;
const ROTATION_HANDLE_HALF_PX = 13;

type Props = {
  region: SchemaRegion;
  selectedId: number | null;
  onSelect: (elementId: number | null) => void;
  onMove: (elementId: number, x: number, y: number) => void;
  onRotate: (element: SchemaElement, rotation: number) => void;
  /** Завъртането с дръжката е спряно от съседна маса (`blocker`) и е пуснато така. */
  onRotateBlocked: (blocker: SchemaElement) => void;
  onDuplicate: (element: SchemaElement) => void;
  onDelete: (element: SchemaElement) => void;
  onAddTable: () => void;
  /** `dropPoint` — логическата точка, където е пуснат обектът. */
  onAddObject: (kind: ObjectKind, dropPoint?: { x: number; y: number }) => void;
  /** Платното пише тук средата на видимата част — там отиват новите елементи. */
  viewCenterRef: MutableRefObject<(() => LogicalPoint) | null>;
};

/**
 * Платното на редактора върху общата сцена (`components/restaurant-schema/
 * SchemaStage`): безкрайно платно, което се мести с влачене по пода и
 * колелцето, а „Побери" вписва всички елементи. Тук се добавят влаченето на
 * елемент с помощни линии за подравняване (`useElementDrag`), изборът с
 * лентата над него, свободното завъртане с дръжката под него
 * (`useElementRotate`) и плаващата лента за добавяне отдолу. Координатите на
 * елементите са логически и не са ограничени до региона.
 */
const SchemaCanvas = ({
  region,
  selectedId,
  onSelect,
  onMove,
  onRotate,
  onRotateBlocked,
  onDuplicate,
  onDelete,
  onAddTable,
  onAddObject,
  viewCenterRef,
}: Props) => {
  const { t } = useTranslation();
  // Лентата за добавяне плава отдолу → повече място под схемата. На широко
  // платно (`isWide`) лентата и зуумът са на един ред долу, на тясно зуумът
  // отива горе вдясно, а подсказката на лентата се скрива.
  const stage = useSchemaStage({ region, roomForToolbar: true, alwaysPannable: true });
  const { scale } = stage;
  viewCenterRef.current = stage.viewCenter;

  const { dragPosition, bindElement } = useElementDrag({
    scale,
    elements: region.elements,
    onMove,
    onSelect,
  });
  const rotation = useElementRotate({ elements: region.elements, onRotate, onBlocked: onRotateBlocked });

  // Докато се върти, елементът се рисува с новия ъгъл (още незаписан).
  const shownRegion: SchemaRegion = rotation.preview
    ? {
        ...region,
        elements: region.elements.map((element) =>
          element.id === rotation.preview?.elementId ? { ...element, rotation: rotation.preview.rotation } : element,
        ),
      }
    : region;

  // Докато се влачи, елементът следва пръста.
  const positionOf = (element: SchemaElement) =>
    dragPosition?.elementId === element.id
      ? { x: dragPosition.x, y: dragPosition.y }
      : { x: element.x, y: element.y };

  const selected = shownRegion.elements.find((element) => element.id === selectedId);
  // Записаният елемент (без незаписания ъгъл) — от него „Отмени" помни стария ъгъл.
  const savedSelected = region.elements.find((element) => element.id === selectedId);
  const isRotating = rotation.preview?.active === true;

  // Обект, пуснат от лентата: точката на екрана → логическа (навсякъде по платното).
  const handleDropObject = (kind: ObjectKind, clientX: number, clientY: number) => {
    const point = stage.clientToLogical(clientX, clientY);
    if (point) onAddObject(kind, { x: snapToGrid(point.x), y: snapToGrid(point.y) });
  };

  return (
    <SchemaStage
      stage={stage}
      region={shownRegion}
      selectedId={selectedId}
      positionOf={positionOf}
      onCanvasTap={() => onSelect(null)}
      getElementProps={(element) => ({
        ...bindElement(element),
        className: `touch-none ${
          dragPosition?.elementId === element.id
            ? "z-10 cursor-grabbing opacity-90"
            : "cursor-pointer"
        }`,
      })}
      emptyState={
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 px-6 text-center">
          <span className="text-[15px] font-bold tracking-[-0.2px] text-ink">
            {t("restaurantSchema.canvas.emptyTitle")}
          </span>
          <span className="text-[12.5px] font-medium text-muted">
            {t("restaurantSchema.canvas.emptyHint")}
          </span>
        </div>
      }
      renderOverlay={(layout) => {
        if (dragPosition && dragPosition.guides.length > 0) {
          return <AlignmentGuideLines guides={dragPosition.guides} scale={scale} origin={stage.origin} />;
        }
        if (!selected || !savedSelected || dragPosition?.elementId === selected.id) return null;
        const box = layout.boxOf(selected);
        const center = { x: box.centerX, y: box.centerY };
        const restingHandle = { x: box.centerX, y: box.bottom + ROTATION_HANDLE_GAP_PX };
        const preview = rotation.preview?.elementId === selected.id ? rotation.preview : null;
        const handle = preview?.active
          ? {
              x: center.x + preview.radius * Math.cos((preview.handleAngle * Math.PI) / 180),
              y: center.y + preview.radius * Math.sin((preview.handleAngle * Math.PI) / 180),
            }
          : restingHandle;
        // Лентата стои точно над ръба на завъртения елемент (със столовете);
        // до горния ръб на платното — под дръжката, иначе се реже.
        const above = box.top - TOOLBAR_GAP_PX;
        const below = above < TOOLBAR_HEIGHT;
        return (
          <>
            {!isRotating && (
              <SelectionToolbar
                left={box.centerX}
                top={below ? restingHandle.y + ROTATION_HANDLE_HALF_PX + TOOLBAR_GAP_PX : above}
                below={below}
                onDuplicate={() => onDuplicate(selected)}
                onDelete={() => onDelete(selected)}
              />
            )}
            <RotationHandle
              handle={handle}
              stemFrom={preview?.active ? center : { x: box.centerX, y: box.bottom }}
              rotation={selected.rotation}
              active={isRotating}
              handlers={rotation.bindHandle(savedSelected, center, handle)}
            />
          </>
        );
      }}
    >
      <AddToolbar
        showHint={stage.isWide}
        onAddTable={onAddTable}
        onAddObject={(kind) => onAddObject(kind)}
        onDropObject={handleDropObject}
      />
    </SchemaStage>
  );
};

export default SchemaCanvas;
